/**
 * Where FLOU is and when somebody is there.
 *
 * Two surfaces need it now — the contact page's "wpadnij do nas" panel and the
 * footer of every page — so it stopped being a pair of constants inside one
 * component. An address that appears twice on a site and disagrees with itself
 * is the kind of thing nobody notices until a customer drives to the wrong
 * village.
 *
 * This is the PLACE, not the legal entity. Who is selling — the registered
 * name, the NIP, the REGON — lives in `@/lib/seller`, is read from the
 * environment, and renders as nothing at all until it is filled in. The two
 * are deliberately apart: the office exists today and the company registration
 * does not, and a footer must be able to say the first without inventing the
 * second.
 */

import { clockLabel, openingFor } from '@repo/db/office-hours'

/** The address, and the string a maps query needs to land on the building. */
export const OFFICE = {
  street: 'Kwiatowa 8',
  postcode: '55-010',
  town: 'Radomierzyce',
  near: 'pod Wrocławiem',
  query: 'Kwiatowa 8, 55-010 Radomierzyce',
} as const

/**
 * Directions, opened in whatever maps app the visitor already uses.
 *
 * Google's documented `dir` endpoint with no key and no origin: leaving the
 * start point out lets the phone use where the person actually is, which is
 * the only honest default.
 */
export const DIRECTIONS_HREF = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
  OFFICE.query,
)}`

/** One day of the week, as both surfaces need to print it. */
export type OfficeDay = {
  /** Full name — the contact page lists all seven. */
  day: string
  /** Two letters, for the footer's collapsed ranges. */
  short: string
  /** The hours, or `null` when the office is shut. */
  open: string | null
}

/**
 * Opening hours, day by day, and DAY BY DAY is the point.
 *
 * It began as one line — "poniedziałek – piątek, 9:00–17:00" — which is the
 * same information in the wrong shape, and was also wrong: Friday keeps its own
 * hours, an exception a range has nowhere to put. A range is something the
 * reader has to unpack; a list is something they scan for their own day.
 *
 * These are the hours somebody is HERE, not an invitation to turn up. Both
 * surfaces print the appointment rule beside them for that reason.
 *
 * ── THE NUMBERS ARE NOT HERE ANY MORE ────────────────────────────────────
 * They are in `@repo/db/office-hours`, as minutes, because the SEED needs the
 * same week: it publishes these hours as the studios' bookable windows, and
 * the checkout measures every booking against those rows. Two copies of one
 * timetable is exactly the drift this file exists to prevent — the version a
 * customer reads would go on saying "sobota 8:00 – 22:00" while the calendar
 * refused Saturdays.
 *
 * What stays here is the PRESENTATION: Polish day names, the two-letter forms
 * the footer folds, and `null` for a day nobody is in.
 */
const DAY_NAMES: readonly { weekday: number; day: string; short: string }[] = [
  { weekday: 1, day: 'Poniedziałek', short: 'pn' },
  { weekday: 2, day: 'Wtorek', short: 'wt' },
  { weekday: 3, day: 'Środa', short: 'śr' },
  { weekday: 4, day: 'Czwartek', short: 'czw' },
  { weekday: 5, day: 'Piątek', short: 'pt' },
  { weekday: 6, day: 'Sobota', short: 'sob' },
  { weekday: 0, day: 'Niedziela', short: 'nd' },
]

export const OFFICE_HOURS: readonly OfficeDay[] = DAY_NAMES.map(({ weekday, day, short }) => {
  const open = openingFor(weekday)
  return {
    day,
    short,
    open: open ? `${clockLabel(open.opensAt)} – ${clockLabel(open.closesAt)}` : null,
  }
})

/**
 * The same hours, folded into runs — "pn – czw 9:00–17:00".
 *
 * For the footer, where seven rows would be a third of the column. Only
 * CONSECUTIVE days with identical hours fold together, which is the property
 * that keeps the fold honest: if Wednesday ever closes early, the run breaks
 * around it and the footer grows a row rather than quietly averaging.
 *
 * A run of one keeps its full name. "pt" alone is a label somebody has to
 * decode; "Piątek" is a word.
 */
export function compactHours(
  hours: readonly OfficeDay[] = OFFICE_HOURS,
): { label: string; open: string | null }[] {
  const runs: OfficeDay[][] = []
  for (const entry of hours) {
    const last = runs.at(-1)
    if (last && last[0]?.open === entry.open) last.push(entry)
    else runs.push([entry])
  }

  return runs.flatMap((run) => {
    const first = run[0]
    const final = run.at(-1)
    if (!first || !final) return []
    return [
      {
        label: run.length === 1 ? first.day : `${first.short} – ${final.short}`,
        open: first.open,
      },
    ]
  })
}
